import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Quote, QuoteStore } from '@/types';
import api from '@/lib/api';
import { inlineError } from '@/lib/errorReport';

export const useQuoteStore = create<QuoteStore>()(
  devtools(
    (set) => {
      let quoteFetchRequestId = 0;

      return {
      quotes: [],
      currentQuote: null,
      quotesLoading: false,
      quotesError: null,
      quoteLoading: false,
      quoteError: null,

      fetchQuotes: async (params) => {
        set({ quotesLoading: true, quotesError: null });
        try {
          const quotes = await api.getQuotes(params);
          set({ quotes, quotesLoading: false });
        } catch (error) {
          set({
            quotesError: error instanceof Error ? error.message : 'Failed to fetch quotes',
            quotesLoading: false,
          });
        }
      },

      fetchQuote: async (id: string) => {
        const requestId = ++quoteFetchRequestId;
        set({ quoteLoading: true, quoteError: null });
        try {
          const quote = await api.getQuote(id);
          if (requestId !== quoteFetchRequestId) return;
          set({ currentQuote: quote, quoteLoading: false });
        } catch (error) {
          if (requestId !== quoteFetchRequestId) return;
          set({
            currentQuote: null,
            quoteError: inlineError('De offerte kon niet worden geladen', error),
            quoteLoading: false,
          });
        }
      },

      createQuote: async (data: Partial<Quote>) => {
        set({ quoteLoading: true, quoteError: null });
        try {
          const quote = await api.createQuote(data);
          set((state) => ({
            quotes: [...state.quotes, quote],
            currentQuote: quote,
            quoteLoading: false,
          }));
          return quote;
        } catch (error) {
          set({
            // De aanroeper meldt de fout zelf.
            quoteError: inlineError('De offerte kon niet worden aangemaakt'),
            quoteLoading: false,
          });
          throw error;
        }
      },

      updateQuote: async (id: string, data: Partial<Quote>) => {
        try {
          const quote = await api.updateQuote(id, data);
          set((state) => ({
            quotes: state.quotes.map((q) => (q.id === id ? quote : q)),
            currentQuote: state.currentQuote?.id === id ? quote : state.currentQuote,
          }));
        } catch (error) {
          console.error('Failed to update quote:', error);
          throw error;
        }
      },

      setQuotesArchived: async (ids: string[], archived: boolean) => {
        if (archived) await api.archiveQuotes(ids);
        else await api.restoreQuotes(ids);
        const idSet = new Set(ids);
        const archivedAt = archived ? new Date() : null;
        set((state) => ({
          quotes: state.quotes.map((q) => (idSet.has(q.id) ? { ...q, archivedAt } : q)),
        }));
      },

      setCurrentQuote: (quote: Quote | null) => {
        quoteFetchRequestId++;
        set({ currentQuote: quote });
      },
      };
    },
    { name: 'QuoteStore' }
  )
);
